import { Component } from 'react';
import styled, { css } from 'styled-components';
import { fontSize, radius } from '@/public/template/common/css/setting/variable';

type ButtonType = 'simple' | 'normal' | 'primary' | 'disabled' | 'delete';
type ButtonSize = 'middle' | 'wide' | 'full';
type ButtonHeight = 'normal' | 'large';

interface IProps {
  text: string;
  readonly children?: React.ReactNode;
  type: ButtonType;
  size?: ButtonSize; // middle:文字数に合わせて伸縮 wide:80% full:100%
  heightSize?: ButtonHeight; // large：フィード読み込みなど高さのあるパターン
  isDisabled: boolean; // buttonタグのdisable
  linkURL?: string;
  onClick?: () => void;
  useAnchorTag: boolean;
  dataClParams?: string;
  hasUal?: boolean;
  linkTarget?: '_blank' | '_parent';
}

const ButtonWrapper = styled.span<{ readonly $size: ButtonSize }>`
  text-align: center;

  ${({ $size }) => {
    switch ($size) {
      case 'middle':
        return css`
          display: inline-block;
        `;
      case 'wide':
      case 'full':
        return css`
          display: block;
        `;
      default:
        return;
    }
  }}
`;

const ButtonStyle = css<{
  readonly $type: ButtonType;
  readonly $size: ButtonSize;
  readonly $height: ButtonHeight;
}>`
  && {
    display: block;
    font-size: ${fontSize.px14};
    font-weight: bold;
    border-radius: ${radius.middle_common};

    &:hover {
      text-decoration: none;
      cursor: pointer;
    }

    ${({ $size }) => {
      switch ($size) {
        case 'middle':
          return css`
            padding: 8px 32px;
            padding-right: 32px;
            padding-left: 32px;
          `;
        case 'wide':
          return css`
            width: 80%;
            padding-right: 8px;
            padding-left: 8px;
            margin: 0 auto;
          `;
        case 'full':
          return css`
            width: 100%;
            padding-right: 8px;
            padding-left: 8px;
            margin: 0 auto;
          `;
        default:
          return;
      }
    }}

    ${({ theme, $type }) => {
      switch ($type) {
        case 'simple':
          return css`
            color: ${theme.news.palette.btn_simple_text_common};
            background-color: ${theme.news.palette.btn_simple_bg_common};
            border: solid 1px ${theme.news.palette.btn_simple_border_common};

            &:hover {
              background-color: ${theme.news.palette.bg_hover_normal_pc};
            }
          `;
        case 'normal':
          return css`
            color: ${theme.news.palette.btn_normal_text_common};
            background-color: ${theme.news.palette.btn_normal_bg_common};
            border: solid 1px ${theme.news.palette.btn_normal_border_common};

            &:hover {
              background-color: ${theme.news.palette.bg_hover_normal_pc};
            }
          `;
        case 'primary':
          return css`
            color: ${theme.news.palette.btn_primary_text_common};
            background-color: ${theme.news.palette.btn_primary_bg_common};
            border: solid 1px ${theme.news.palette.btn_primary_bg_common}; /* borderがないとsimpleボタンと高さがずれるため */

            &:hover {
              span {
                opacity: 0.5;
              }
            }
          `;
        case 'disabled':
          return css`
            color: ${theme.news.palette.btn_disabled_text_common};
            background-color: ${theme.news.palette.btn_disabled_bg_common};
            border: solid 1px ${theme.news.palette.btn_disabled_bg_common}; /* borderがないとsimpleボタンと高さがずれるため */

            /* hoverの表示を打ち消す */
            &:hover {
              text-decoration: none;
              pointer-events: none;
              cursor: default;
            }
          `;
        case 'delete':
          return css`
            color: ${theme.news.palette.btn_delete_text_common};
            background-color: ${theme.news.palette.btn_delete_bg_common};
            border: solid 1px ${theme.news.palette.btn_delete_border_common}; /* borderがないとsimpleボタンと高さがずれるため */

            &:hover {
              span {
                opacity: 0.5;
              }
            }
          `;

        default:
          return;
      }
    }}

${({ $height }) => {
      switch ($height) {
        case 'normal':
          return css`
            padding-top: 8px;
            padding-bottom: 8px;
          `;
        case 'large':
          return css`
            padding-top: 16px;
            padding-bottom: 16px;
          `;
        default:
          return;
      }
    }}
  }
`;

const StyledButton = styled.button<{
  readonly $type: ButtonType;
  readonly $size: ButtonSize;
  readonly $height: ButtonHeight;
}>`
  ${ButtonStyle}
`;

const StyledAnchor = styled.a<{
  readonly $type: ButtonType;
  readonly $size: ButtonSize;
  readonly $height: ButtonHeight;
}>`
  ${ButtonStyle}
`;

/**
 * 共通ボタン コンポーネント
 */
export default class Button extends Component<IProps> {
  public render() {
    // UALでログ取得するかの判定
    const goToContentAttribute = this.props.hasUal ? { 'data-ual-gotocontent': '' } : {};
    // useAnchorTagがtrueのときでも、typeがdisableのときは、aタグではなくボタンタグで表示する
    const useAnchorTag = this.props.useAnchorTag && this.props.type !== 'disabled';
    return (
      // size指定のない場合はmiddle
      <ButtonWrapper $size={this.props.size || 'middle'}>
        {useAnchorTag ? (
          // 指定がある場合はaタグを使う
          <StyledAnchor
            $type={this.props.type}
            $size={this.props.size || 'middle'}
            $height={this.props.heightSize || 'normal'}
            href={this.props.linkURL}
            data-cl-params={this.props.dataClParams}
            {...goToContentAttribute}
            target={this.props.linkTarget ?? ''}
          >
            <span>{this.props.text}</span>
            {this.props.children ? this.props.children : null}
          </StyledAnchor>
        ) : (
          // 通常はbuttonタグを使う
          <StyledButton
            $type={this.props.type}
            $size={this.props.size || 'middle'}
            $height={this.props.heightSize || 'normal'}
            data-cl-params={this.props.dataClParams}
            {...goToContentAttribute}
            onClick={this.props.onClick}
            disabled={this.props.isDisabled}
          >
            <span>{this.props.text}</span>
            {this.props.children ? this.props.children : null}
          </StyledButton>
        )}
      </ButtonWrapper>
    );
  }
}
